// ── Capstone admin ("/capstone", "/capstone/submissions") — Wave 8 (🔒 dark) ─
// The ADMIN capstone view: the submissions list/detail + the upload → conversion
// → evaluation → certificate-trigger pipeline statuses + the pending_schema
// maturity-gate. This surface is HONEST about three backend realities (all
// verified against apps/vjal-coaching-api):
//   1. NO capstone read route → the submissions LIST renders a NOT-CONFIGURED
//      honest state and NO read fires (we never fabricate submission rows).
//   2. The agent-eval lane is DARK → conversion/evaluation render as
//      UNAVAILABLE/processing, never a fake score.
//   3. The structured JSON schema is an OPEN DEPENDENCY → pending_schema is
//      MATURITY-GATED; we show only schemaStatus/version/reference, no invented
//      schema fields.
//   4. The certificate trigger has NO backend → NOT-CONFIGURED.
//
// ── PARTICIPANT EXCLUSION (Wave 8 release gate) ──────────────────────────────
// A `participant` is DENIED the admin capstone module entirely (route +
// page fail-closed). The participant-facing capstone UPLOAD is a separate Wave-9
// surface that will be honest ("not available until a participant submit route
// exists") because there is NO participant-scoped submit endpoint — only the
// STAFF intake POST. This page never offers a participant submit.

import { Alert, Button, IconChart, Module, ModuleBody, ModuleHead, PageFrame } from "@vjal/ui";
import { DataTableCollection } from "@vjal/ui"
import { useMemo } from "react";

import { HonestState } from "../../components/honest-state";
import type { ProductScreenContext } from "../product-route-contracts";
import { buildCapstoneColumns, type CapstoneStageRow } from "./capstone-columns";
import { CapstoneSubmissionsSection } from "./capstone-submissions-section";
import {
  CAPSTONE_PIPELINE,
  CAPSTONE_SCHEMA_STATE,
  canAccessCapstoneModule,
  canSubmitCapstoneIntake
} from "./capstone-helpers";

export function CapstonePage({ role, routePath }: ProductScreenContext) {
  const moduleDenied = !canAccessCapstoneModule(role);
  const canIntake = canSubmitCapstoneIntake(role);
  const columns = useMemo(() => buildCapstoneColumns(), []);
  const stages = useMemo<CapstoneStageRow[]>(() => CAPSTONE_PIPELINE.map((stage) => ({ ...stage })), []);

  // ── Fail closed: a denied role (participant) gets permission-denied. ────────
  if (moduleDenied) {
    return (
      <section aria-label="Capstone" className="col-span-12" data-testid="capstone-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture="permission-denied"
            icon={<IconChart size={22} />}
            title="You don't have access to capstone administration"
            message="The capstone admin is for coaching staff and assigned leads. Your own capstone is part of your dashboard."
          />
        </div>
      </section>
    );
  }

  return (
    <PageFrame title="Capstone" subtitle="Staff capstone pipeline, submissions, and maturity gates." className="col-span-12">
      <section aria-label="Capstone" className="flex flex-col gap-4" data-testid="capstone-page">
      {/* ── Submissions list — LIVE read (F15), scope-isolated, reportId reference ── */}
      <CapstoneSubmissionsSection routePath={routePath} />

      {/* Intake — STAFF-ONLY. The intake POST exists (202) but enqueues onto the
          DARK agent lane; it is the only capstone write. Non-staff roles never see
          it. There is NO participant submit route (Wave-9 gap). */}
      {canIntake ? (
        <Module span={12}>
          <ModuleHead title="Capstone intake" subtitle="Staff intake enqueues evaluation; the agent lane is dark." />
          <ModuleBody data-testid="capstone-intake">
            <div data-honest-posture="gated">
              <Alert variant="info">
                Staff intake records a capstone artifact and enqueues evaluation (202). Evaluation runs on the agent lane,
                which is currently dark — results appear once it's enabled.
              </Alert>
              <Button type="button" variant="secondary" disabled aria-label="Record capstone intake" className="mt-2">
                Intake runs from a cohort (evaluation lane dark)
              </Button>
            </div>
          </ModuleBody>
        </Module>
      ) : null}

      {/* ── The processing pipeline (contract reference, honest postures) ───── */}
      <Module span={8} className="overflow-visible">
        <ModuleHead
          title="Processing pipeline"
          subtitle="Upload → conversion → evaluation → certificate trigger. Each stage shows its honest state today."
        />
        <ModuleBody className="min-h-0 auto" data-testid="capstone-pipeline">
          {/* Mobile residual (U-COACH-RESIDUAL-C1 Gemini): Processing pipeline
              What happens cells hard-clip mid-sentence without scroll affordance.
              Outer scroller + w-max min-width shell keeps columns readable. */}
          <div
            className="relative -mx-1 min-w-0 max-w-full overflow-x-auto overscroll-x-contain rounded-lg border border-line px-1 shadow-scroll-hint"
            aria-label="Scrollable capstone processing pipeline"
          >
            <div className="w-max min-w-[48rem]" data-testid="capstone-pipeline-table-scroller">
              <DataTableCollection
                columns={columns}
                data={stages}
                rowKey={(row) => row.id}
                searchable={false}
                caption="Capstone processing stages and their current lane state"
                className="min-w-[48rem]"
                tableClassName="w-max min-w-[48rem] max-w-none [&_th]:whitespace-nowrap [&_td]:max-w-none"
                stableChipNames
              />
            </div>
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent sm:hidden"
            />
          </div>
        </ModuleBody>
      </Module>

      {/* ── pending_schema — MATURITY-GATED (no invented JSON fields) ───────── */}
      <Module span={4}>
        <ModuleHead title="Evaluation schema" subtitle="The structured capstone result schema is an open dependency." />
        <ModuleBody data-testid="capstone-schema">
          <div data-honest-posture="maturity-gated" className="py-2">
            <HonestState
              posture="maturity-gated"
              icon={<IconChart size={22} />}
              title="Schema pending"
              message="The structured capstone evaluation schema isn't finalized yet (status: pending_schema). Submissions are accepted and queued, but evaluation results render only once the schema is defined — no result fields are shown until then."
            />
          </div>
          <div className="mt-3 space-y-1 text-sm text-ink-muted">
            <div className="flex justify-between gap-4">
              <span>Schema status</span>
              <span className="text-right font-medium text-ink-body">{CAPSTONE_SCHEMA_STATE.status}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span>Version</span>
              <span className="text-right font-medium text-ink-body">{CAPSTONE_SCHEMA_STATE.version ?? "—"}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span>Reference</span>
              <span className="text-right font-medium text-ink-body">{CAPSTONE_SCHEMA_STATE.reference ?? "—"}</span>
            </div>
          </div>
        </ModuleBody>
      </Module>
      </section>
    </PageFrame>
  );
}
